import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, SelectChangeEvent } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFacilityLayoutData } from "@context";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import {
  buildTopLevelFacilityOptions,
  getFacilityHierarchyId,
  getSelectedFacilityFromSession,
  notifySelectedFacilitySessionUpdated,
  SELECTED_FACILITY_STORAGE_KEY,
  SELECTED_FACILITY_UPDATED_EVENT,
} from "@utils/facilityUtils";
import { isHoldingCompanyNetwork } from "@utils/networkFacilityRows";
import { resolveFacilityNetworkView } from "@utils/facilityNetworkView";
import { getActingParentFacilityId } from "./facilityNetwork/facilityNetworkHierarchyUtils";
import {
  getMyVerticalContext,
  getPreviewVertical,
  type VerticalContextResponse,
} from "@services/verticals";
import {
  batchLookupNpis,
  confirmChildLinkRequests,
  uploadBatchLookupNpis,
  type BatchNpiLookupResult,
  type ConfirmChildLinkResult,
} from "@services/facilityNetworkLink";
import FacilityNetworkFacilityHeader from "./facilityNetwork/FacilityNetworkFacilityHeader";
import FacilityNetworkBackButton from "./facilityNetwork/FacilityNetworkBackButton";
import FacilityNetworkFigmaHeading from "./facilityNetwork/FacilityNetworkFigmaHeading";
import AddChildFacilitySearchPanel from "./facilityNetwork/AddChildFacilitySearchPanel";
import BulkAddCsvDialog from "./facilityNetwork/BulkAddCsvDialog";
import NpiBatchReviewScreen from "./facilityNetwork/NpiBatchReviewScreen";
import NpiLinkSuccessPanel from "./facilityNetwork/NpiLinkSuccessPanel";
import FacilityNetworkPageShell from "./facilityNetwork/FacilityNetworkPageShell";
import {
  formatProcessedNpiCount,
  formatReviewProcessedSubtitle,
  countProcessedNpis,
  enrichBatchReviewFacilityNames,
  isUsableReviewFacilityName,
} from "./facilityNetwork/addChildFacilityReviewUtils";
import { npiLookupService } from "@services/npiLookup";
import { getSelectedCountryCode } from "@utils/countryUtils";
import {
  mapFoundNpiFacility,
  mapNotFoundNpi,
  mapNpiResultToSearchHit,
  mergeSelectedChildFacilities,
  rematchHitsToLocalFacilities,
  type ChildFacilitySearchHit,
} from "./facilityNetwork/addChildFacilitySearchUtils";
import { FN } from "./facilityNetwork/facilityNetworkTheme";

type NetworkFacility = MyFacilityListRow & {
  facility_id?: number;
};

type PageStep = "entry" | "review" | "success";

function myFacilityRowToNetworkFacility(row: MyFacilityListRow): NetworkFacility {
  return {
    ...row,
    id: row.facility_id ?? row.id,
  };
}

function findFacilityById(
  facilities: NetworkFacility[],
  facilityId: number,
): NetworkFacility | undefined {
  for (const facility of facilities) {
    const id = getFacilityHierarchyId(facility);
    if (id === facilityId) return facility;
    const child = (facility.child_facilities || []).find(
      (item) => getFacilityHierarchyId(item) === facilityId,
    ) as NetworkFacility | undefined;
    if (child) return child;
  }
  return facilities.find((f) => getFacilityHierarchyId(f) === facilityId);
}

function storeSelectedFacility(facility: NetworkFacility | undefined): void {
  if (typeof window === "undefined") return;
  if (!facility) {
    sessionStorage.removeItem(SELECTED_FACILITY_STORAGE_KEY);
    notifySelectedFacilitySessionUpdated();
    return;
  }
  sessionStorage.setItem(SELECTED_FACILITY_STORAGE_KEY, JSON.stringify(facility));
  notifySelectedFacilitySessionUpdated();
}

export default function AddChildFacilitiesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as {
    selectedFacility?: NetworkFacility;
  } | null;
  const { myFacilities, loading: layoutLoading } = useFacilityLayoutData();
  const [previewVerticalSlug, setPreviewVerticalSlug] = useState<string | null>(
    () => getPreviewVertical(),
  );
  const [verticalContext, setVerticalContext] = useState<VerticalContextResponse | null>(null);
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | "">("");
  const [selectedHits, setSelectedHits] = useState<ChildFacilitySearchHit[]>([]);
  const [step, setStep] = useState<PageStep>("entry");
  const [review, setReview] = useState<BatchNpiLookupResult | null>(null);
  const [successResult, setSuccessResult] = useState<ConfirmChildLinkResult | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void Promise.resolve(getMyVerticalContext())
      .then((ctx) => {
        if (!cancelled) setVerticalContext(ctx);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ slug: string | null }>).detail;
      setPreviewVerticalSlug(detail?.slug ?? null);
    };
    window.addEventListener("unicomply:preview-vertical-changed", handler as EventListener);
    return () => {
      window.removeEventListener("unicomply:preview-vertical-changed", handler as EventListener);
    };
  }, []);

  const activeVertical = useMemo<string>(() => {
    if (previewVerticalSlug) return previewVerticalSlug;
    return verticalContext?.effective_vertical || verticalContext?.user_vertical || "healthcare";
  }, [previewVerticalSlug, verticalContext]);

  const visibleFacilities = useMemo(
    () =>
      myFacilities
        .filter(
          (row) =>
            (row.vertical || "healthcare") === activeVertical && !isHoldingCompanyNetwork(row),
        )
        .map(myFacilityRowToNetworkFacility),
    [myFacilities, activeVertical],
  );

  const groupedFacilityOptions = useMemo(
    () => buildTopLevelFacilityOptions(visibleFacilities),
    [visibleFacilities],
  );

  const resolveInitialFacilityId = useCallback((): number | "" => {
    const routeFacility = routeState?.selectedFacility;
    if (routeFacility) {
      const routeId = getFacilityHierarchyId(routeFacility);
      if (routeId != null && findFacilityById(visibleFacilities, routeId)) {
        return routeId;
      }
    }
    const stored = getSelectedFacilityFromSession<NetworkFacility>();
    if (stored) {
      const storedId = getFacilityHierarchyId(stored);
      if (storedId != null && findFacilityById(visibleFacilities, storedId)) {
        return storedId;
      }
    }
    const first = visibleFacilities[0];
    return first ? (getFacilityHierarchyId(first) ?? "") : "";
  }, [routeState?.selectedFacility, visibleFacilities]);

  useEffect(() => {
    if (!visibleFacilities.length) {
      setSelectedFacilityId("");
      storeSelectedFacility(undefined);
      return;
    }
    const initialId = resolveInitialFacilityId();
    setSelectedFacilityId(initialId);
    if (initialId !== "") {
      const facility = findFacilityById(visibleFacilities, Number(initialId));
      if (facility) storeSelectedFacility(facility);
    }
  }, [visibleFacilities, resolveInitialFacilityId]);

  useEffect(() => {
    const onSelectedUpdated = () => {
      const stored = getSelectedFacilityFromSession<NetworkFacility>();
      if (!stored) return;
      const storedId = getFacilityHierarchyId(stored);
      if (storedId == null) return;
      if (findFacilityById(visibleFacilities, storedId)) {
        setSelectedFacilityId(storedId);
      }
    };
    window.addEventListener(SELECTED_FACILITY_UPDATED_EVENT, onSelectedUpdated);
    return () => {
      window.removeEventListener(SELECTED_FACILITY_UPDATED_EVENT, onSelectedUpdated);
    };
  }, [visibleFacilities]);

  const selectedFacility = useMemo(
    () =>
      selectedFacilityId === ""
        ? undefined
        : findFacilityById(visibleFacilities, Number(selectedFacilityId)),
    [visibleFacilities, selectedFacilityId],
  );

  const networkView = useMemo(() => {
    if (!selectedFacility) return null;
    return resolveFacilityNetworkView(selectedFacility, visibleFacilities);
  }, [selectedFacility, visibleFacilities]);

  const parentFacilityId = useMemo(
    () => getActingParentFacilityId(networkView),
    [networkView],
  );

  const handleFacilityChange = (event: SelectChangeEvent<number | "">) => {
    const raw = event.target.value;
    if (raw === "") {
      setSelectedFacilityId("");
      storeSelectedFacility(undefined);
      return;
    }
    const facilityId = typeof raw === "number" ? raw : Number(raw);
    const facility = findFacilityById(visibleFacilities, facilityId);
    setSelectedFacilityId(facilityId);
    storeSelectedFacility(facility);
  };

  const goToFacilityNetwork = (tab?: "children" | "link-request") => {
    navigate("/dashboard/facilities", {
      state: {
        currentView: "facility-network",
        networkTab: tab,
        selectedFacility,
      },
    });
  };

  const resetFlow = () => {
    setStep("entry");
    setReview(null);
    setSuccessResult(null);
    setSelectedHits([]);
  };

  const returnToEntryKeepingSelection = () => {
    setStep("entry");
    setReview(null);
  };

  const applyReviewResult = (result: BatchNpiLookupResult) => {
    setReview(result);
    setStep("review");
  };

  const lookupHitForReview = async (npi: string): Promise<ChildFacilitySearchHit | null> => {
    try {
      const response = await npiLookupService.lookupNPI({
        npi_number: npi,
        country_code: getSelectedCountryCode(),
      });
      const result = response.data?.results?.[0];
      return result ? mapNpiResultToSearchHit(result) : null;
    } catch {
      return null;
    }
  };

  const prepareReviewResult = async (result: BatchNpiLookupResult) => {
    const { review: enriched, hits } = await enrichBatchReviewFacilityNames(
      result,
      lookupHitForReview,
    );
    const namedNotFound = (enriched.save_for_invite || enriched.not_found).map((npi) => {
      const registryName = enriched.facility_name_by_npi?.[npi];
      const fromHit = hits.find((hit) => hit.npi === npi);
      if (fromHit && isUsableReviewFacilityName(fromHit.name, npi)) return fromHit;
      if (isUsableReviewFacilityName(registryName, npi)) {
        return {
          npi,
          name: (registryName || "").trim(),
          city: "",
          state: "",
          ccn: "",
        } satisfies ChildFacilitySearchHit;
      }
      return mapNotFoundNpi(npi);
    });
    const incoming = [...enriched.found.map(mapFoundNpiFacility), ...namedNotFound];
    setSelectedHits((prev) => mergeSelectedChildFacilities(prev, incoming));
    applyReviewResult(enriched);
  };

  const runConfirm = async (
    found: string[],
    notFound: string[],
    inviteEmailByNpi?: Record<string, string>,
  ) => {
    if (!parentFacilityId) return;
    setConfirming(true);
    try {
      const result = await confirmChildLinkRequests(
        parentFacilityId,
        found,
        notFound,
        inviteEmailByNpi,
      );
      setSuccessResult(result);
      setStep("success");
      toast.success("Selections processed");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to confirm requests.");
    } finally {
      setConfirming(false);
    }
  };

  const handleRemoveReviewItem = (
    bucket: "found" | "not_found" | "invalid" | "duplicate",
    id: string,
  ) => {
    if (!review) return;
    const nextFound = bucket === "found" ? review.found.filter((item) => item.npi !== id) : review.found;
    const nextNotFound = bucket === "not_found" ? review.not_found.filter((npi) => npi !== id) : review.not_found;
    const nextWillSend = bucket === "found" && review.will_send ? review.will_send.filter((item) => item.npi !== id) : nextFound;
    const nextSaveForInvite = bucket === "not_found" && review.save_for_invite ? review.save_for_invite.filter((npi) => npi !== id) : nextNotFound;
    const nextDupWarnings = review.duplicate_warnings.filter((item) => item.npi !== id);
    const nextInvalid = review.invalid.filter((item) => item.value !== id);
    const nextWontSend = review.wont_send
      ? review.wont_send.filter((item) => item.identifier !== id)
      : [
          ...nextDupWarnings.map((w) => ({
            identifier: w.npi,
            reason: w.message,
            status: w.status,
          })),
          ...nextInvalid.map((inv) => ({
            identifier: inv.value,
            reason: inv.reason,
            status: "invalid",
          })),
        ];

    const nextReview: BatchNpiLookupResult = {
      ...review,
      found: nextFound,
      not_found: nextNotFound,
      will_send: nextWillSend,
      save_for_invite: nextSaveForInvite,
      duplicate_warnings: nextDupWarnings,
      invalid: nextInvalid,
      wont_send: nextWontSend,
    };
    setReview(nextReview);
    setSelectedHits((prev) => prev.filter((hit) => hit.npi !== id));
  };

  const handleReview = async () => {
    if (parentFacilityId == null || selectedHits.length === 0) return;
    const rematched = rematchHitsToLocalFacilities(selectedHits, visibleFacilities);
    setSelectedHits(rematched);
    const list = rematched.map((hit) => hit.npi);
    setReviewing(true);
    try {
      const result = await batchLookupNpis(parentFacilityId, list);
      await prepareReviewResult(result);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to review NPIs.");
    } finally {
      setReviewing(false);
    }
  };

  const handleFileSelected = async (file: File): Promise<boolean> => {
    if (!parentFacilityId) return false;
    setReviewing(true);
    try {
      const result = await uploadBatchLookupNpis(parentFacilityId, file);
      await prepareReviewResult(result);
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to process file.");
      return false;
    } finally {
      setReviewing(false);
    }
  };

  if (layoutLoading) {
    return (
      <FacilityNetworkPageShell>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 240,
          }}
        >
          <CircularProgress sx={{ color: FN.primary }} />
        </Box>
      </FacilityNetworkPageShell>
    );
  }

  return (
    <FacilityNetworkPageShell>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          width: "100%",
          pb: 0.75,
        }}
      >
        <FacilityNetworkFacilityHeader
          selectedFacilityId={selectedFacilityId}
          selectedFacility={selectedFacility}
          groupedFacilityOptions={groupedFacilityOptions}
          onFacilityChange={handleFacilityChange}
        />

        <Box
          sx={{
            mt: FN.headerToTabs,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 2,
          }}
        >
          <FacilityNetworkBackButton
            variant="link"
            label={
              step === "entry"
                ? "Back to Linked Facilities"
                : "Back to add child facilities"
            }
            onClick={
              step === "entry"
                ? () => goToFacilityNetwork("children")
                : step === "review"
                  ? returnToEntryKeepingSelection
                  : resetFlow
            }
          />

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              data-testid="add-child-content"
              sx={{
                width: "100%",
                maxWidth: step === "entry" ? 800 : "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <FacilityNetworkFigmaHeading
                title={
                  step === "success"
                    ? review
                      ? formatProcessedNpiCount(countProcessedNpis(review))
                      : "Review complete"
                    : step === "entry"
                      ? "Add Child Facilities"
                      : "Review upload results"
                }
                subtitle={
                  step === "success"
                    ? undefined
                    : step === "review" && review
                      ? formatReviewProcessedSubtitle(review)
                      : "Search by CCN, NPI, or facility name. Nothing is sent until you review."
                }
              />

            {!visibleFacilities.length ? (
              <Alert severity="info">
                Add an active facility before sending child link requests.
              </Alert>
            ) : !selectedFacility ? (
              <Alert severity="info">Select a facility to continue.</Alert>
            ) : parentFacilityId == null ? (
              <Alert severity="info">
                Child facilities cannot add child facilities.
              </Alert>
            ) : step === "success" && successResult ? (
              <NpiLinkSuccessPanel
                result={successResult}
                wontSendRows={review?.wont_send}
                onGoToNetwork={() => goToFacilityNetwork("children")}
                onGoToLinkRequests={() => goToFacilityNetwork("link-request")}
              />
            ) : step === "review" && review ? (
              <NpiBatchReviewScreen
                review={review}
                selectedHits={selectedHits}
                onBack={returnToEntryKeepingSelection}
                onConfirm={() =>
                  runConfirm(
                    (review.will_send || review.found).map((item) => item.npi),
                    review.save_for_invite || review.not_found,
                  )
                }
                onRemoveItem={handleRemoveReviewItem}
                confirming={confirming}
              />
            ) : (
              <AddChildFacilitySearchPanel
                selected={selectedHits}
                onSelectedChange={setSelectedHits}
                onReview={handleReview}
                onCancel={() => goToFacilityNetwork("children")}
                onOpenBulkAdd={() => setBulkAddOpen(true)}
                onFileUpload={handleFileSelected}
                reviewing={reviewing}
                reviewDisabled={parentFacilityId == null}
                parentFacilityId={parentFacilityId}
                localFacilities={visibleFacilities}
              />
            )}
            </Box>
          </Box>
        </Box>
      </Box>
      <BulkAddCsvDialog
        open={bulkAddOpen}
        reviewing={reviewing}
        onClose={() => setBulkAddOpen(false)}
        onReview={handleFileSelected}
      />
    </FacilityNetworkPageShell>
  );
}
